import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addCourseToMenuType,
  removeCourseFromMenuType,
  reorderMenuTypeCourse,
  getAvailableCoursesForMenuType,
  getMenuTypeCourses,
  menuService
} from '../utils/api';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for fetching courses for a menu type
 */
export const useMenuTypeCourses = (menuTypeId, customOptions = {}) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['menuTypeCourses', menuTypeId, language],
    queryFn: async () => {
      // Add a cache-busting timestamp to prevent disk cache issues
      const timestamp = Date.now();
      return menuService.getMenuTypeCourses(menuTypeId, language, timestamp);
    },
    enabled: !!menuTypeId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true, // Always refetch when component mounts
    ...customOptions
  });
};

/**
 * Custom hook for fetching available courses that can be added to a menu type
 */
export const useAvailableCoursesForMenuType = (menuTypeId, schoolId, customOptions = {}) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['availableCoursesForMenuType', menuTypeId, schoolId, language],
    queryFn: async () => {
      // Add a cache-busting timestamp to prevent disk cache issues
      const timestamp = Date.now();
      return getAvailableCoursesForMenuType(menuTypeId, language, timestamp);
    },
    enabled: !!menuTypeId && !!schoolId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true, // Always refetch when component mounts
    ...customOptions
  });
};

/**
 * Custom hook for adding a course to a menu type with optimistic updates
 */
export const useAddCourseToMenuType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: ({ menuTypeId, courseId }) => 
      addCourseToMenuType(menuTypeId, courseId),
    
    onMutate: async ({ menuTypeId, courseId, courseName, schoolId }) => {
      console.log('[OptimisticUpdate] onMutate called with:', { menuTypeId, courseId, courseName, schoolId });
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuTypeCourses', menuTypeId, language],
      });
      await queryClient.cancelQueries({
        queryKey: ['availableCoursesForMenuType', menuTypeId, schoolId, language],
      });
      await queryClient.cancelQueries({
        queryKey: ['menuTypes', schoolId],
      });

      // Snapshot previous values
      const previousMenuTypeCourses = queryClient.getQueryData(['menuTypeCourses', menuTypeId, language]);
      const previousAvailableCourses = queryClient.getQueryData(['availableCoursesForMenuType', menuTypeId, schoolId, language]);
      const previousMenuTypes = queryClient.getQueryData(['menuTypes', schoolId]);

      // Optimistically update menu type courses
      if (previousMenuTypeCourses) {
        // Find the maximum position to place the new course at the end
        const maxPosition = previousMenuTypeCourses.reduce(
          (max, course) => Math.max(max, course.position || 0), 
          0
        );

        const optimisticCourse = {
          id: courseId,
          menu_type_id: menuTypeId,
          name: courseName || 'Loading...',
          position: maxPosition + 1,
        };

        queryClient.setQueryData(
          ['menuTypeCourses', menuTypeId, language],
          [...previousMenuTypeCourses, optimisticCourse]
        );
      }

      // Optimistically remove from available courses
      if (previousAvailableCourses) {
        queryClient.setQueryData(
          ['availableCoursesForMenuType', menuTypeId, schoolId, language],
          previousAvailableCourses.filter(course => course.id !== courseId)
        );
      }

      // Optimistically update menu types to increment course count
      if (previousMenuTypes) {
        const updatedMenuTypes = previousMenuTypes.map(menuType => 
          menuType.id === menuTypeId 
            ? { ...menuType, courseCount: (menuType.courseCount || 0) + 1 }
            : menuType
        );
        console.log('[OptimisticUpdate] Adding course - updating menuTypes cache:', {
          menuTypeId,
          previousMenuTypes: previousMenuTypes.map(mt => ({ id: mt.id, name: mt.name, courseCount: mt.courseCount })),
          updatedMenuTypes: updatedMenuTypes.map(mt => ({ id: mt.id, name: mt.name, courseCount: mt.courseCount }))
        });
        queryClient.setQueryData(
          ['menuTypes', schoolId],
          updatedMenuTypes
        );
      } else {
        console.log('[OptimisticUpdate] Adding course - previousMenuTypes is null/undefined, forcing immediate invalidation to trigger banner update');
        // Force invalidation to trigger immediate refetch and banner update
        queryClient.invalidateQueries({
          queryKey: ['menuTypes', schoolId],
        });
      }

      return { previousMenuTypeCourses, previousAvailableCourses, previousMenuTypes };
    },
    
    onError: (err, { menuTypeId, schoolId }, context) => {
      // If the mutation fails, revert to the previous values
      if (context?.previousMenuTypeCourses) {
        queryClient.setQueryData(
          ['menuTypeCourses', menuTypeId, language],
          context.previousMenuTypeCourses
        );
      }
      
      if (context?.previousAvailableCourses) {
        queryClient.setQueryData(
          ['availableCoursesForMenuType', menuTypeId, schoolId, language],
          context.previousAvailableCourses
        );
      }
      
      if (context?.previousMenuTypes) {
        queryClient.setQueryData(
          ['menuTypes', schoolId],
          context.previousMenuTypes
        );
      }
    },
    
    onSettled: (data, error, { menuTypeId, schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuTypeCourses', menuTypeId],
      });
      queryClient.invalidateQueries({
        queryKey: ['availableCoursesForMenuType', menuTypeId, schoolId],
      });
      
      // Also invalidate menu types since the available courses changed
      queryClient.invalidateQueries({
        queryKey: ['menuTypes', schoolId],
      });
      
      // Also invalidate publicMenu cache to ensure public-facing menus are updated
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
    },
  });
};

/**
 * Custom hook for removing a course from a menu type with optimistic updates
 */
export const useRemoveCourseFromMenuType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: ({ menuTypeId, courseId }) => 
      removeCourseFromMenuType(menuTypeId, courseId),
    
    onMutate: async ({ menuTypeId, courseId, courseName, schoolId }) => {
      console.log('[OptimisticUpdate] onMutate called with:', { menuTypeId, courseId, courseName, schoolId });
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuTypeCourses', menuTypeId, language],
      });
      
      if (schoolId) {
        await queryClient.cancelQueries({
          queryKey: ['availableCoursesForMenuType', menuTypeId, schoolId, language],
        });
        await queryClient.cancelQueries({
          queryKey: ['menuTypes', schoolId],
        });
      }

      // Snapshot previous values
      const previousMenuTypeCourses = queryClient.getQueryData(['menuTypeCourses', menuTypeId, language]);
      const previousAvailableCourses = schoolId ? 
        queryClient.getQueryData(['availableCoursesForMenuType', menuTypeId, schoolId, language]) : null;
      const previousMenuTypes = schoolId ? 
        queryClient.getQueryData(['menuTypes', schoolId]) : null;

      // Get the course being removed for potential addition to available courses
      let removedCourse = null;
      
      // Optimistically update menu type courses
      if (previousMenuTypeCourses) {
        // Find the course being removed to preserve its data
        removedCourse = previousMenuTypeCourses.find(course => course.id === courseId);
        
        queryClient.setQueryData(
          ['menuTypeCourses', menuTypeId, language],
          previousMenuTypeCourses.filter(course => course.id !== courseId)
        );
      }

      // Optimistically add to available courses if we have the school ID
      if (previousAvailableCourses && removedCourse) {
        queryClient.setQueryData(
          ['availableCoursesForMenuType', menuTypeId, schoolId, language],
          [...previousAvailableCourses, {
            id: courseId,
            name: removedCourse.name || courseName || 'Loading...',
            school_id: schoolId,
          }]
        );
      }

      // Optimistically update menu types to decrement course count
      if (previousMenuTypes) {
        queryClient.setQueryData(
          ['menuTypes', schoolId],
          previousMenuTypes.map(menuType => 
            menuType.id === menuTypeId 
              ? { ...menuType, courseCount: Math.max((menuType.courseCount || 0) - 1, 0) }
              : menuType
          )
        );
      }

      return { 
        previousMenuTypeCourses, 
        previousAvailableCourses,
        previousMenuTypes,
        removedCourse
      };
    },
    
    onError: (err, { menuTypeId, schoolId }, context) => {
      // If the mutation fails, revert to the previous values
      if (context?.previousMenuTypeCourses) {
        queryClient.setQueryData(
          ['menuTypeCourses', menuTypeId, language],
          context.previousMenuTypeCourses
        );
      }
      
      if (context?.previousAvailableCourses) {
        queryClient.setQueryData(
          ['availableCoursesForMenuType', menuTypeId, schoolId, language],
          context.previousAvailableCourses
        );
      }
      
      if (context?.previousMenuTypes) {
        queryClient.setQueryData(
          ['menuTypes', schoolId],
          context.previousMenuTypes
        );
      }
    },
    
    onSettled: (data, error, { menuTypeId, schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuTypeCourses', menuTypeId],
      });
      
      if (schoolId) {
        queryClient.invalidateQueries({
          queryKey: ['availableCoursesForMenuType', menuTypeId, schoolId],
        });
        
        // Also invalidate menu types since the available courses changed
        queryClient.invalidateQueries({
          queryKey: ['menuTypes', schoolId],
        });
      }
      
      // Also invalidate publicMenu cache to ensure public-facing menus are updated
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
    },
  });
};

/**
 * Custom hook for reordering courses in a menu type with optimistic updates
 */
export const useReorderMenuTypeCourse = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: ({ menuTypeId, courseId, direction }) => 
      reorderMenuTypeCourse(menuTypeId, courseId, direction),
    
    onMutate: async ({ menuTypeId, courseId, direction }) => {
      console.log('[OptimisticUpdate] onMutate called with:', { menuTypeId, courseId, direction });
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuTypeCourses', menuTypeId, language],
      });

      // Snapshot the previous value
      const previousMenuTypeCourses = queryClient.getQueryData(['menuTypeCourses', menuTypeId, language]);

      if (!previousMenuTypeCourses) {
        return { previousMenuTypeCourses };
      }

      // Create a copy for optimistic update
      const updatedMenuTypeCourses = [...previousMenuTypeCourses];
      
      // Find the course to move
      const courseIndex = updatedMenuTypeCourses.findIndex(course => course.id === courseId);
      
      if (courseIndex === -1) {
        return { previousMenuTypeCourses };
      }
      
      // Determine new index based on direction
      const newIndex = direction === 'up' 
        ? Math.max(0, courseIndex - 1) 
        : Math.min(updatedMenuTypeCourses.length - 1, courseIndex + 1);
      
      // No change if already at the edge
      if (newIndex === courseIndex) {
        return { previousMenuTypeCourses };
      }
      
      // Swap the courses
      const courseToMove = updatedMenuTypeCourses[courseIndex];
      const courseToSwapWith = updatedMenuTypeCourses[newIndex];
      
      // Swap positions
      const tempPosition = courseToMove.position;
      courseToMove.position = courseToSwapWith.position;
      courseToSwapWith.position = tempPosition;
      
      // Swap in the array for visual effect
      updatedMenuTypeCourses[courseIndex] = courseToSwapWith;
      updatedMenuTypeCourses[newIndex] = courseToMove;
      
      // Sort by position to ensure consistent order
      updatedMenuTypeCourses.sort((a, b) => a.position - b.position);
      
      // Update the cache
      queryClient.setQueryData(
        ['menuTypeCourses', menuTypeId, language],
        updatedMenuTypeCourses
      );

      return { previousMenuTypeCourses };
    },
    
    onError: (err, { menuTypeId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousMenuTypeCourses) {
        queryClient.setQueryData(
          ['menuTypeCourses', menuTypeId, language],
          context.previousMenuTypeCourses
        );
      }
    },
    
    onSettled: (data, error, { menuTypeId, schoolId }) => {
      // No longer refetching menuTypeCourses to prevent stale disk cache issues
      // The optimistic update in onMutate is sufficient
      
      // Also invalidate menu types if school ID is provided
      if (schoolId) {
        queryClient.invalidateQueries({
          queryKey: ['menuTypes', schoolId],
        });
      }
      
      // Also invalidate publicMenu cache to ensure public-facing menus are updated
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
    },
  });
}; 